import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getEffectiveUnits,
  getPiecesForUnitFromUnits,
  getAvailableStock,
  getMaxUnitsFor,
  getUnitPriceFromUnits,
} from '../utils/formatStock';

export const productKey = (p) => p?.productId || p?._id || p?.id;
export const isSellable = (p) => p && p.active !== false && p.isActive !== false;

/**
 * Map anything handed to the till (a product card, a search hit, a quick-add
 * row) onto a real, sellable inventory product. Quick-add rows carry no
 * productId, so they are matched by name/barcode against the loaded catalogue
 * instead of being charged against an arbitrary product.
 */
export const resolveSellableProduct = (candidate, products) => {
  if (!candidate) return null;
  const sellable = (products || []).filter(isSellable);

  const key = productKey(candidate);
  if (key) {
    const exact = sellable.find((p) => String(productKey(p)) === String(key));
    if (exact) return exact;
  }

  const name = String(candidate.productName || candidate.name || '').trim().toLowerCase();
  const code = String(candidate.barcode || '').trim().toLowerCase();
  if (!name && !code) return null;

  return sellable.find((p) => {
    const pName = String(p.productName || p.name || '').trim().toLowerCase();
    const pCode = String(p.barcode || p.serialNumber || '').trim().toLowerCase();
    return (name && pName === name) || (code && pCode === code);
  }) || null;
};

/** Number of sellable sale units a product offers (>1 means a unit must be picked). */
export const sellableUnitCount = (product) => getEffectiveUnits(product).filter((u) => u.sellable).length;

/** Unit a single-tap add should use. */
export const defaultSaleUnit = (product) => getEffectiveUnits(product)[0]?.name || 'Piece';

/**
 * The single source of truth for till cart state, stock validation and
 * pricing. Shared by the POS screen and the Quick Checkout screen so both
 * enforce exactly the same rules.
 */
export function usePosCart({ products = [], dayClosed = false, taxRate = 0, onUnitRequired } = {}) {
  const [cartItems, setCartItems] = useState([]);
  const [discount, setDiscount] = useState('');

  // Base pieces reserved in the cart, per product. The backend validates the
  // same total, so the till can never be sent over the available stock.
  const cartPieces = useMemo(() => {
    const map = new Map();
    for (const item of cartItems) {
      const key = productKey(item);
      const pieces = item.piecesDeducted
        || getPiecesForUnitFromUnits(item.saleUnit, item.quantity || 1, getEffectiveUnits(item));
      map.set(key, (map.get(key) || 0) + (Number(pieces) || 0));
    }
    return map;
  }, [cartItems]);

  const findProduct = useCallback(
    (key) => products.find((p) => String(productKey(p)) === String(key)) || null,
    [products]
  );

  /** Stock guard shared by add / update / checkout. Returns the allowed cap. */
  const maxQuantityFor = useCallback((item, { excludeSelf = true } = {}) => {
    const key = productKey(item);
    const product = findProduct(key) || item;
    const available = getAvailableStock(product);
    const ownPieces = excludeSelf
      ? (item.piecesDeducted
        || getPiecesForUnitFromUnits(item.saleUnit, item.quantity || 1, getEffectiveUnits(item)))
      : 0;
    const reservedElsewhere = Math.max(0, (cartPieces.get(key) || 0) - ownPieces);
    const perUnit = Math.max(
      1,
      (item.piecesDeducted || getPiecesForUnitFromUnits(item.saleUnit, 1, getEffectiveUnits(item)))
        / Math.max(1, item.quantity || 1)
    );
    return {
      available,
      perUnit,
      maxQuantity: Math.max(0, Math.floor((available - reservedElsewhere) / perUnit)),
    };
  }, [cartPieces, findProduct]);

  const addToCart = useCallback((rawProduct) => {
    if (dayClosed) {
      toast.error("Today's session is closed. Sale End was performed — sales are not allowed.");
      return null;
    }
    const product = resolveSellableProduct(rawProduct, products);
    if (!product) {
      toast.error(`"${rawProduct?.productName || rawProduct?.name || 'Item'}" is not in inventory. Add it from Inventory first.`);
      return null;
    }
    if (getAvailableStock(product) <= 0) {
      toast.error(`${product.productName || product.name} is out of stock`);
      return null;
    }
    if (sellableUnitCount(product) > 1) {
      // Multi-unit products (pack/box) must go through the unit selector.
      onUnitRequired?.(product);
      return null;
    }

    const key = productKey(product);
    const unit = defaultSaleUnit(product);
    const reserved = cartPieces.get(key) || 0;
    if (reserved + 1 > getAvailableStock(product)) {
      toast.error(`Only ${getAvailableStock(product)} left in stock for ${product.productName || product.name}`);
      return null;
    }

    setCartItems((prev) => {
      const existing = prev.find((i) => productKey(i) === key && i.saleUnit === unit);
      if (existing) {
        return prev.map((i) => (productKey(i) === key && i.saleUnit === unit
          ? { ...i, quantity: (i.quantity || 1) + 1, piecesDeducted: (i.piecesDeducted || 1) + 1 }
          : i));
      }
      return [...prev, { ...product, quantity: 1, saleUnit: unit, piecesDeducted: 1 }];
    });
    return { ...product, saleUnit: unit };
  }, [dayClosed, products, cartPieces, onUnitRequired]);

  const addWithUnit = useCallback((product, { unit, quantity, price }) => {
    if (dayClosed) {
      toast.error("Today's session is closed. Sales are not allowed.");
      return null;
    }
    const sellable = resolveSellableProduct(product, products);
    if (!sellable) {
      toast.error('This item is not in inventory.');
      return null;
    }

    const key = productKey(sellable);
    const maxUnits = getMaxUnitsFor(sellable, unit);
    if (maxUnits <= 0) {
      toast.error(`${sellable.productName || sellable.name} is out of stock`);
      return null;
    }

    const alreadyForUnit = cartItems
      .filter((i) => productKey(i) === key && i.saleUnit === unit)
      .reduce((sum, i) => sum + (i.quantity || 0), 0);

    let finalQuantity = quantity || 0;
    if (alreadyForUnit + finalQuantity > maxUnits) {
      finalQuantity = Math.max(0, maxUnits - alreadyForUnit);
      toast.error(`Only ${maxUnits} ${unit} available in stock`);
    }
    if (finalQuantity <= 0) return null;

    const piecesDeducted = getPiecesForUnitFromUnits(unit, finalQuantity, getEffectiveUnits(sellable));
    const line = {
      ...sellable,
      productId: sellable.productId || key,
      quantity: finalQuantity,
      saleUnit: unit,
      piecesDeducted,
    };
    if (price > 0) line.salePrice = price;

    setCartItems((prev) => {
      const existing = prev.find((i) => productKey(i) === key && i.saleUnit === unit);
      if (existing) {
        return prev.map((i) => (productKey(i) === key && i.saleUnit === unit
          ? {
            ...i,
            quantity: (i.quantity || 0) + finalQuantity,
            piecesDeducted: (i.piecesDeducted || 0) + piecesDeducted,
          }
          : i));
      }
      return [...prev, line];
    });
    return line;
  }, [dayClosed, products, cartItems]);

  const updateQuantity = useCallback((item, quantity) => {
    const next = Math.max(0, quantity);
    const key = productKey(item);
    if (next === 0) {
      removeItemByKey(key, item.saleUnit, setCartItems);
      return;
    }
    const { available, perUnit, maxQuantity } = maxQuantityFor(item);
    const capped = Math.min(next, maxQuantity);
    if (capped < next) {
      toast.error(`Only ${available} in stock – quantity set to ${capped}`);
    }
    setCartItems((prev) => prev.map((i) => (productKey(i) === key && i.saleUnit === item.saleUnit
      ? { ...i, quantity: capped, piecesDeducted: Math.round(capped * perUnit) }
      : i)));
  }, [maxQuantityFor]);

  const removeItem = useCallback((item) => {
    removeItemByKey(productKey(item), item.saleUnit, setCartItems);
    toast.success('Item removed from cart');
  }, []);

  /**
   * The POS custom amount mechanism: a line's `salePrice` is what the screen
   * shows, what the totals multiply, and what is sent to the backend as
   * `unitPrice` (honoured by packagingService.resolveSaleUnit). Passing null
   * restores the product's own selling price.
   */
  const setLinePrice = useCallback((item, amount) => {
    const key = productKey(item);
    const product = findProduct(key) || item;
    // "Restore" must come from the product's own unit price, never from the
    // override that is already sitting on the line.
    const ownPrice = getUnitPriceFromUnits(product, item.saleUnit) || product.salePrice || product.price || 0;
    const next = (amount === null || amount === undefined || amount === '')
      ? ownPrice
      : Math.max(0, Number(amount) || 0);
    setCartItems((prev) => prev.map((i) => (productKey(i) === key && i.saleUnit === item.saleUnit
      ? { ...i, salePrice: next }
      : i)));
    return next;
  }, [findProduct]);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setDiscount('');
  }, []);

  const linePrice = (item) => item.salePrice || item.price || 0;
  const lineTotal = (item) => linePrice(item) * (item.quantity || 1);

  const subtotal = useMemo(
    () => cartItems.reduce(
      (sum, item) => sum + linePrice(item) * (item.quantity || 1),
      0
    ),
    [cartItems]
  );

  const taxAmount = useMemo(
    () => (subtotal - (parseFloat(discount) || 0)) * (taxRate / 100),
    [subtotal, discount, taxRate]
  );

  const total = useMemo(() => subtotal - (parseFloat(discount) || 0) + taxAmount, [subtotal, discount, taxAmount]);

  /** First line whose cart total exceeds the stock on hand. */
  const findStockViolation = useCallback(() => cartItems.find((item) => {
    const key = productKey(item);
    const product = findProduct(key);
    if (!product) return true;
    return (cartPieces.get(key) || 0) > getAvailableStock(product);
  }), [cartItems, cartPieces, findProduct]);

  return {
    cartItems,
    setCartItems,
    cartPieces,
    discount,
    setDiscount,
    subtotal,
    taxAmount,
    total,
    linePrice,
    lineTotal,
    addToCart,
    addWithUnit,
    updateQuantity,
    removeItem,
    setLinePrice,
    clearCart,
    maxQuantityFor,
    findStockViolation,
  };
}

function removeItemByKey(key, saleUnit, setCartItems) {
  setCartItems((prev) => prev.filter((i) => !(productKey(i) === key && i.saleUnit === saleUnit)));
}
